export interface AxisRange {
  min: number;
  max: number;
  interval: number;
}

export interface ComputeNiceAxisRangeOptions {
  /** Roughly how many ticks the axis should end up with (actual count may vary slightly). */
  targetTicks?: number;
  /** Extra headroom added above and below the data extent, as a fraction of the data span. */
  paddingRatio?: number;
  /** Lower bound the axis may never go below (e.g. 0 for counts that can't be negative). */
  floor?: number;
  /** Smallest data span to plan around, so near-flat series still get a legible range. */
  minSpan?: number;
}

/**
 * Rounds `value` to a "nice" number (1, 2, 5, or 10 times a power of 10), per the
 * classic Heckbert algorithm used for graph axis labels. When `round` is true the
 * nearest nice number is used (for tick spacing); otherwise the smallest nice number
 * that is >= value is used (for the overall range).
 */
function niceNumber(value: number, round: boolean): number {
  if (value <= 0) return 0;

  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * 10 ** exponent;
}

/** Rounds away floating-point noise (e.g. 199.99999999999997) introduced by repeated division/multiplication. */
function cleanFloat(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * Derives a readable y-axis range (min, max, tick interval) from a set of visible
 * data values. Unlike clamping the axis to [0, max], this pads the observed extent
 * so variation stays legible, and only touches zero when the data (plus padding)
 * actually reaches it.
 */
export function computeNiceAxisRange(
  values: number[],
  options: ComputeNiceAxisRangeOptions = {}
): AxisRange {
  const { targetTicks = 5, paddingRatio = 0.2, floor, minSpan = 1 } = options;

  const finiteValues = values.filter((value) => Number.isFinite(value));
  const dataMin = finiteValues.length > 0 ? Math.min(...finiteValues) : 0;
  const dataMax = finiteValues.length > 0 ? Math.max(...finiteValues) : minSpan;

  const span = Math.max(dataMax - dataMin, minSpan);
  const padding = span * paddingRatio;

  let paddedMin = dataMin - padding;
  const paddedMax = dataMax + padding;

  if (floor != null) paddedMin = Math.max(floor, paddedMin);

  const roughRange = niceNumber(Math.max(paddedMax - paddedMin, Number.EPSILON), false);
  const interval = niceNumber(roughRange / Math.max(targetTicks - 1, 1), true) || minSpan;

  let niceMin = Math.floor(paddedMin / interval) * interval;
  const niceMax = Math.ceil(paddedMax / interval) * interval;

  if (floor != null) niceMin = Math.max(floor, niceMin);

  return { min: cleanFloat(niceMin), max: cleanFloat(niceMax), interval: cleanFloat(interval) };
}
